import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysSavedFilter } from '../queryKeys';

import {
  type UpdateSavedFilterMutationOptions,
  type UpdateSavedFilterMutationVariables,
} from './types';
import { updateSavedFilter } from './updateSavedFilter';

/**
 * React Query based custom hook for updating saved filters.
 *
 * Invalidates the saved-filter list for the updated filter type on settle.
 *
 * @param options - Optional mutation options.
 * @returns The mutation result.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/User/operation/update_saved_filter_user_saved_filters__saved_filter_id__patch
 */
export const useMutationUpdateSavedFilter = (
  options?: UpdateSavedFilterMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      savedFilterId,
      type: _type,
      ...payload
    }: UpdateSavedFilterMutationVariables) =>
      updateSavedFilter(savedFilterId, payload),
    onSettled(_data, _error, { type }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysSavedFilter.getAll(type),
      });
    },
    ...options,
  });
};
